import { useEffect } from "react";
import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardOverview } from "@/components/dashboard/DashboardOverview";
import { MobileMenuProvider } from "@/contexts/MobileMenuContext";
import { StudentsList } from "@/components/students/StudentsList";
import { WorkoutsList } from "@/components/workouts/WorkoutsList";
import { WorkoutForm } from "@/components/workouts/WorkoutForm";
import { TrainingSessionsList } from "@/components/training-sessions/TrainingSessionsList";
import { NutritionList } from "@/components/nutrition/NutritionList";
import { NutritionForm } from "@/components/nutrition/NutritionForm";
import { AssessmentsList } from "@/components/assessments/AssessmentsList";
import { AssessmentForm } from "@/components/assessments/AssessmentForm";
import { Settings } from "@/components/settings/Settings";
import { Profile } from "@/components/profile/Profile";
import NewStudent from "./NewStudent";
import NewWorkout from "./NewWorkout";
import EditWorkout from "./EditWorkout";
import NewTrainingSession from "./NewTrainingSession";
import { TrainingSessionDetails } from "@/components/training-sessions/TrainingSessionDetails";
import { ProgressList } from "@/components/progress/ProgressList";
import { StudentProgress } from "@/components/progress/StudentProgress";
import { ScheduleCalendar } from "@/components/schedule/Calendar";
import Plan from "./Plan";
import { MobileNavBar } from "@/components/shared/MobileNavBar";
import { getDashboardNavItems } from "@/components/shared/navigation-items";
import { StudentRequestsList } from "@/components/student-requests/StudentRequestsList";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath, getLanguagePath } from "@/utils/language";
import EditStudent from "./EditStudent";
import { ExerciseLibraryPage } from "./ExerciseLibraryPage";
import { MessagesPage } from "@/components/messaging/MessagesPage";

const PORTUGUESE_COUNTRIES = [
  "PT", "BR", "AO", "MZ", "GW", "CV", "ST", "TL", "GQ"
];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();

  useEffect(() => {
    if (!user) {
      navigate(getPath("/auth"));
      return;
    }

    // Verificar se estamos na URL correta do idioma
    const currentLangPrefix = location.pathname.split('/')[1];
    const needsRedirect = (
      (language === 'pt' && currentLangPrefix !== 'pt') ||
      (language === 'es' && currentLangPrefix !== 'es') ||
      (language === 'fr' && currentLangPrefix !== 'fr') ||
      (language === 'en' && ['pt', 'es', 'fr'].includes(currentLangPrefix))
    );

    if (needsRedirect) {
      // Extrair o caminho sem o prefixo de idioma
      let pathWithoutPrefix = location.pathname;
      if (['pt', 'es', 'fr'].includes(currentLangPrefix)) {
        pathWithoutPrefix = '/' + location.pathname.split('/').slice(2).join('/');
      }
      
      // Navegue para o caminho correto com o prefixo de idioma apropriado
      navigate(getLanguagePath(pathWithoutPrefix, language), { replace: true });
    }
  }, [user, navigate, getPath, language, location.pathname]);

  if (!user) return null;
  return (
    <MobileMenuProvider>
      <div className="min-h-screen bg-gradient-to-tr from-[#F9F9F9] via-[#F0F4F9] to-[#FCFCFC] dark:bg-gradient-to-tr dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-x-hidden pb-20 md:pb-0 relative">
        <div className="absolute inset-0 w-full h-full z-0 opacity-25 dark:opacity-10" style={{
          backgroundImage: 'radial-gradient(#0000001a 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} />
        
        <DashboardHeader />
        <div className="flex overflow-x-hidden">
          <DashboardSidebar />
          <main className="flex-1 p-4 md:p-6 ml-0 md:ml-[18rem] mt-20 relative z-0 w-full max-w-full overflow-x-hidden">
            <div className="container mx-auto max-w-[1400px] overflow-x-hidden px-0">
              <div className="rounded-xl relative z-10 w-full p-2 md:p-0 px-px">
                <Routes>
                  <Route path="/" element={<DashboardOverview />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/students" element={<StudentsList />} />
                  <Route path="/students/new" element={<NewStudent />} />
                  <Route path="/students/edit/:id" element={<EditStudent />} />
                  <Route path="/student-requests" element={<StudentRequestsList />} />
                  <Route path="/workouts" element={<WorkoutsList />} />
                  <Route path="/workouts/new" element={<NewWorkout />} />
                  <Route path="/workouts/edit/:id" element={<EditWorkout />} />
                  <Route path="/training-sessions" element={<TrainingSessionsList />} />
                  <Route path="/training-sessions/new" element={<NewTrainingSession />} />
                  <Route path="/training-sessions/:id" element={<TrainingSessionDetails />} />
                  <Route path="/nutrition" element={<NutritionList />} />
                  <Route path="/nutrition/new" element={<NutritionForm />} />
                  <Route path="/nutrition/edit/:id" element={<NutritionForm />} />
                  <Route path="/assessments" element={<AssessmentsList />} />
                  <Route path="/assessments/new" element={<AssessmentForm />} />
                  <Route path="/assessments/edit/:id" element={<AssessmentForm />} />
                  <Route path="/progress" element={<ProgressList />} />
                  <Route path="/progress/:id" element={<StudentProgress />} />
                  <Route path="/schedule" element={<ScheduleCalendar />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/plan" element={<Plan />} />
                  <Route path="/exercise-library" element={<ExerciseLibraryPage />} />
                  <Route path="/messages" element={<MessagesPage />} />
                </Routes>
              </div>
            </div>
          </main>
        </div>
        <MobileNavBar dashboardType="trainer" />
      </div>
    </MobileMenuProvider>
  );
};

export default Dashboard;
